import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  Sparkles,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  XCircle,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  History,
  Zap,
  Lightbulb,
  Activity,
  Target,
} from 'lucide-react';
import {
  generateContextualPrompts,
  dismissPrompt,
  type ContextualPrompt,
} from '../lib/contextual-prompts';
import { motion, fadeIn, slideInFromTop, AnimatePresence } from '../lib/motion-utils';
import { useNavigate } from 'react-router';

const iconMap: Record<string, any> = {
  'time-pattern': Clock,
  'behavior-gap': Calendar,
  'relational-pattern': TrendingUp,
  'preventive': Sparkles,
};

// Helper function to get colors based on priority
function getPromptColor(priority: 'high' | 'medium' | 'low') {
  switch (priority) {
    case 'high':
      return {
        border: 'border-red-300',
        bg: 'bg-red-50',
        icon: 'text-red-600',
        text: 'text-red-900',
        button: 'bg-red-600 hover:bg-red-700',
      };
    case 'medium':
      return {
        border: 'border-amber-300',
        bg: 'bg-amber-50',
        icon: 'text-amber-600',
        text: 'text-amber-900',
        button: 'bg-amber-600 hover:bg-amber-700',
      };
    case 'low':
      return {
        border: 'border-blue-300',
        bg: 'bg-blue-50',
        icon: 'text-blue-600',
        text: 'text-blue-900',
        button: 'bg-blue-600 hover:bg-blue-700',
      };
  }
}

export function ContextualPrompts() {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<ContextualPrompt[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    loadPrompts();
    
    // Refresh prompts every 30 minutes
    const interval = setInterval(loadPrompts, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadPrompts = () => {
    const newPrompts = generateContextualPrompts();
    setPrompts(newPrompts);
  };

  const handleDismiss = (promptId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    dismissPrompt(promptId);
    setPrompts(prompts.filter(p => p.id !== promptId));
  };

  const handleAction = (prompt: ContextualPrompt) => {
    const route = typeof prompt.actionRoute === 'string' ? prompt.actionRoute.trim() : '';
    if (!route) {
      navigate('/action');
      return;
    }
    navigate(route.startsWith('/') ? route : `/${route}`);
  };

  if (prompts.length === 0) {
    return null;
  }

  // Show highest priority prompt first
  const sortedPrompts = [...prompts].sort((a, b) => {
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 px-1">
        <Lightbulb className="w-4 h-4 text-purple-600" />
        <h3 className="text-sm font-medium text-neutral-700">Smart Insights</h3>
      </div>

      <AnimatePresence mode="popLayout">
        {sortedPrompts.map((prompt, index) => {
          const Icon = iconMap[prompt.type] || Activity;
          const colors = getPromptColor(prompt.priority);
          const isExpanded = expandedId === prompt.id;

          return (
            <motion.div
              key={prompt.id}
              variants={fadeIn}
              initial="initial"
              animate="animate"
              exit="exit"
              className={`
                relative overflow-hidden rounded-lg border-2 transition-all
                ${colors.border} ${colors.bg}
              `}
            >
              {/* Priority Indicator */}
              <div 
                className={`absolute top-0 left-0 w-1 h-full ${
                  prompt.priority === 'high' ? 'bg-red-600' :
                  prompt.priority === 'medium' ? 'bg-amber-600' :
                  'bg-blue-600'
                }`} 
              />

              <div className="pl-5 pr-4 py-4">
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={`p-2 rounded-lg ${colors.bg} mt-0.5`}>
                    <Icon className={`w-4 h-4 ${colors.icon}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className={`font-medium text-sm ${colors.text}`}>
                        {prompt.title}
                      </h4>
                      
                      <button
                        onClick={(e) => handleDismiss(prompt.id, e)}
                        className="p-1 hover:bg-neutral-200 rounded transition-colors flex-shrink-0"
                        aria-label="Dismiss"
                      >
                        <XCircle className="w-3.5 h-3.5 text-neutral-500" />
                      </button>
                    </div>

                    <p className="text-sm text-neutral-700 leading-relaxed mb-3">
                      {prompt.message}
                    </p>

                    {/* Action Button */}
                    <Button
                      onClick={() => handleAction(prompt)}
                      size="sm"
                      className={`h-8 text-xs ${colors.button} text-white`}
                    >
                      {prompt.action}
                    </Button>

                    {/* Additional Details (if available) */}
                    {prompt.data && (
                      <div className="mt-3 pt-3 border-t border-neutral-200">
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : prompt.id)}
                          className="text-xs text-neutral-600 hover:text-neutral-900 transition-colors"
                        >
                          {isExpanded ? 'Hide' : 'Show'} details
                        </button>
                        
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="mt-2 text-xs text-neutral-600 space-y-1"
                          >
                            {prompt.type === 'time-pattern' && prompt.data.avgScore && (
                              <p>• Average RTI at this time: {Math.round(prompt.data.avgScore)}</p>
                            )}
                            {prompt.type === 'behavior-gap' && prompt.data.lastRTI && (
                              <p>• Last RTI score: {prompt.data.lastRTI}</p>
                            )}
                            {prompt.type === 'relational-pattern' && prompt.data.avgInitiation && (
                              <p>• 7-day average initiation: {Math.round(prompt.data.avgInitiation)}%</p>
                            )}
                            {prompt.type === 'relational-pattern' && prompt.data.increase && (
                              <p>• RTI increase: +{prompt.data.increase} points</p>
                            )}
                            {prompt.data.avgInterval && (
                              <p>• Pattern repeats every ~{Math.round(prompt.data.avgInterval)} days</p>
                            )}
                          </motion.div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Summary Badge */}
      {prompts.length > 1 && (
        <div className="flex items-center justify-center gap-2 text-xs text-neutral-500 pt-2">
          <Target className="w-3.5 h-3.5" />
          <span>{prompts.length} active insight{prompts.length === 1 ? '' : 's'}</span>
        </div>
      )}
    </div>
  );
}